import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BeakerIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  ShoppingBagIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/app/providers/AuthProvider';
import type { MaintenanceHubId, MaintenanceScopeState } from '@/services/maintenanceState';
import { DOCUMENTS_HUB_ENABLED, HR_HUB_ENABLED } from '@/app/router/maintenanceHubMap';
import { MaintenanceCompanionMessage } from './MaintenanceCompanionMessage';

const HUB_DETAILS: Record<MaintenanceHubId, { name: string; path: string; icon: typeof BriefcaseIcon }> = {
  business: { name: 'Business', path: '/business', icon: BriefcaseIcon },
  dipendenze: { name: 'Dipendenze', path: '/dipendenze', icon: BeakerIcon },
  documenti: { name: 'Documenti', path: '/documenti', icon: DocumentTextIcon },
  applicativi: { name: 'Applicativi', path: '/applicativi', icon: ShoppingBagIcon },
  hr: { name: 'HR', path: '/hr', icon: UserGroupIcon },
};

export function HubMaintenancePage({
  hubId,
  state,
}: {
  hubId: MaintenanceHubId;
  state: MaintenanceScopeState;
}) {
  const navigate = useNavigate();
  const { canViewHub } = useAuth();
  const currentHub = HUB_DETAILS[hubId];
  const restoreAt = state.estimatedRestoreAt
    ? new Date(state.estimatedRestoreAt).toLocaleString('it-IT', { dateStyle: 'long', timeStyle: 'short' })
    : null;
  const availableHubs = (Object.entries(HUB_DETAILS) as Array<[MaintenanceHubId, typeof HUB_DETAILS[MaintenanceHubId]]>)
    .filter(([otherHubId]) => otherHubId !== hubId
      && (otherHubId !== 'documenti' || DOCUMENTS_HUB_ENABLED)
      && (otherHubId !== 'hr' || HR_HUB_ENABLED)
      && canViewHub(otherHubId));

  return (
    <main className="mx-auto flex min-h-[calc(100dvh-9rem)] w-full max-w-5xl items-center px-4 py-8 sm:px-7">
      <section className="w-full rounded-2xl bg-white/92 p-6 ring-1 ring-inset ring-zinc-950/[0.08] dark:bg-[#16191c] dark:ring-white/[0.09] sm:p-9" aria-labelledby="hub-maintenance-title">
        <div className="flex flex-wrap items-center gap-3 text-sm font-medium text-slate-600 dark:text-slate-300">
          <WrenchScrewdriverIcon className="h-5 w-5" aria-hidden="true" />
          <span>Stato disponibilità</span>
          <span className="rounded-md border border-slate-300 px-2 py-1 text-xs dark:border-slate-600">In manutenzione</span>
        </div>
        <h1 id="hub-maintenance-title" className="mt-6 text-3xl font-semibold tracking-[-0.035em] text-zinc-950 dark:text-white sm:text-4xl">
          {currentHub.name} è temporaneamente in manutenzione
        </h1>
        <p className="mt-4 max-w-[68ch] text-base leading-relaxed text-zinc-600 dark:text-white/65">
          {state.message || `L’Hub ${currentHub.name} è temporaneamente in manutenzione.`}
        </p>
        <MaintenanceCompanionMessage key={hubId} context="hub" />

        {restoreAt && (
          <div className="mt-6 max-w-md rounded-xl bg-zinc-50 px-4 py-3 ring-1 ring-inset ring-zinc-950/[0.07] dark:bg-white/[0.04] dark:ring-white/[0.08]">
            <p className="text-xs font-medium text-zinc-500 dark:text-white/55">Ripristino previsto, indicativo</p>
            <p className="mt-1 text-sm font-medium text-zinc-800 dark:text-white/85">{restoreAt}</p>
          </div>
        )}
        <p className="mt-5 text-xs leading-relaxed text-zinc-500 dark:text-white/45">
          L’orario è informativo. Il servizio non si riattiva automaticamente.
        </p>
        {availableHubs.length > 0 && (
          <p className="mt-3 text-sm leading-relaxed text-zinc-600 dark:text-white/60">
            Nel frattempo puoi continuare a utilizzare gli altri strumenti disponibili.
          </p>
        )}

        <div className="mt-8 flex flex-wrap gap-3 border-t border-zinc-950/[0.08] pt-6 dark:border-white/[0.09]">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-slate-700 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-500 active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-white"
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            Torna alla Home
          </button>
          {availableHubs.map(([otherHubId, hub]) => {
            const HubIcon = hub.icon;
            return (
              <button
                key={otherHubId}
                type="button"
                onClick={() => navigate(hub.path)}
                className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-zinc-300 px-4 text-sm font-medium text-zinc-800 transition hover:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-500 active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none dark:border-white/15 dark:text-white/85 dark:hover:bg-white/[0.06]"
              >
                <HubIcon className="h-4 w-4" aria-hidden="true" />
                Apri {hub.name}
              </button>
            );
          })}
        </div>
      </section>
    </main>
  );
}
